import React from 'react';

import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { endOfMonth, formatISO } from 'date-fns';
import PropTypes from 'prop-types';

import * as AppStore from '@asteria/datalayer/stores/app';

import {
	filterOperations,
	mergeOperations,
	updateOperations,
} from '../../../../../utils/operations';
import HistoryTooltip from '../../../../tooltip/history';
import { BASE_VALUE } from '../../../constants';
import { useStateHidden, useStateInvisible, useValue } from '../../hooks';
import { getPreviousValue } from '../../utils';

import View from './view';

import '../../styles.scss';

const Bar = React.memo((props) => {
	const { className, type, date, status, category, tag, render } = props;

	const { getValues, setValue } = useFormContext();

	const fmtDate = formatISO(date, { representation: 'date' });
	const value = useValue({ type, status, date, category, tag });

	const hidden = useStateHidden({ type, date, status, category, tag });
	const invisible = useStateInvisible({ type, date, status, category, tag });

	const TooltipProps = React.useMemo(
		() => ({
			tooltip: (
				<HistoryTooltip
					type={type}
					category={category}
					tag={tag}
					date={fmtDate}
				/>
			),
			placement: type === 'deposit' ? 'top' : 'bottom',
		}),
		[category, fmtDate, tag, type],
	);

	const adjustTag = useSelector((store) => {
		const tags = AppStore.selectors.tags(store);
		return tags.find((item) => item?.name === '$adjusted')?._id ?? null;
	});

	const tagId = useSelector((store) => {
		if (tag === '$deposit' || tag === '$withdraw') {
			return null;
		}

		const tags = AppStore.selectors.tags(store);
		const storedTag = tags.find((item) => item?.name === tag);

		return storedTag?._id ?? storedTag?.id ?? '$unknown';
	});

	const onChange = React.useCallback(
		({ value, immediate }) => {
			const existingChanges = getValues('changes') ?? [];
			const allOperations = getValues('allOperations') ?? [];
			const operations = filterOperations(
				mergeOperations(allOperations),
				{
					type,
					tagId,
					startDate: formatISO(date, { representation: 'date' }),
					endDate: formatISO(endOfMonth(date), {
						representation: 'date',
					}),
				},
			);

			const prevOperationsTotal = operations.reduce(
				(acc, curr) => acc + curr.payload.total,
				0,
			);

			let newValue = value;

			if (prevOperationsTotal === 0 && value === 0) {
				const previous = getPreviousValue({
					type,
					tag: tagId,
					date,
					getValues,
				});

				newValue =
					previous?.value?.value ?? previous?.value ?? BASE_VALUE;
			}

			const changes = updateOperations({
				value: newValue,
				date,
				tagId,
				type,
				operations: operations,
				defaultTagId: adjustTag,
			});

			if (!immediate) {
				setValue(`allOperations`, allOperations.concat(changes));
				setValue(`changes`, existingChanges.concat(changes));
				setValue(`intermediateChanges`, []);
			} else {
				setValue(`intermediateChanges`, changes);
			}
		},
		[getValues, setValue, date, type, tagId, adjustTag],
	);

	return (
		<View
			className={className}
			type={type}
			date={date}
			status={status}
			category={category}
			tag={tag}
			tooltip={TooltipProps}
			value={value}
			onChange={onChange}
			hidden={hidden}
			invisible={invisible}
			render={render}
		/>
	);
});

Bar.displayName = 'BarProvider';
Bar.propTypes = {
	className: PropTypes.string,
	onAction: PropTypes.func,

	type: PropTypes.string,
	date: PropTypes.string,
	status: PropTypes.string,
	category: PropTypes.string,
	tag: PropTypes.string,

	render: PropTypes.func,
};

export default Bar;
